import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * Breadcrumb tail for detail pages. The route→crumb map in `TopBar` covers
 * every list route statically; only a detail page knows the entity's NAME, and
 * it knows it asynchronously (after its query resolves). So the page pushes the
 * label up.
 *
 * Lives here rather than in `TopBar` so the provider (rendered by `AppShell`)
 * and the consumers (pages) never import each other.
 */
const CrumbContext = createContext<{
  detail: string | null;
  setDetail: (v: string | null) => void;
}>({ detail: null, setDetail: () => {} });

export function CrumbProvider({ children }: { children: ReactNode }) {
  const [detail, setDetail] = useState<string | null>(null);
  return <CrumbContext.Provider value={{ detail, setDetail }}>{children}</CrumbContext.Provider>;
}

/** TopBar reads the current tail. */
export function useCrumbTail(): string | null {
  return useContext(CrumbContext).detail;
}

/**
 * Detail pages call this with the entity name — `useCrumb(repo?.pathWithNamespace)`.
 * Pass `undefined`/`null` while loading; the crumb simply shows the route label
 * until the name arrives. Clears on unmount so a stale name can't leak onto the
 * next page.
 */
export function useCrumb(label?: string | null): void {
  const { setDetail } = useContext(CrumbContext);
  useEffect(() => {
    setDetail(label ?? null);
    return () => setDetail(null);
  }, [label, setDetail]);
}
